import { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import type { CertificateStatusEnum } from '@share/common-types';
import { FunctionalRootState } from '@share/utils';
import { getUserCertificates } from '@share/store/slices';

interface UseUserCertificatesParams {
  email?: string;
  organizationId?: number;
  statuses?: CertificateStatusEnum[];
  autoFetch?: boolean;
}

interface UseUserCertificatesResult {
  certificates: FunctionalRootState['certificatesStore']['data'];
  loading: boolean;
  error: string;
  fetchCertificates: () => void;
}

export const useUserCertificates = (
  params: UseUserCertificatesParams = {},
): UseUserCertificatesResult => {
  const { email, organizationId, statuses, autoFetch = true } = params;
  const dispatch = useDispatch();
  const certificatesStore = useSelector((state: FunctionalRootState) => state.certificatesStore);

  const statusesKey = statuses?.join(',');

  const fetchCertificates = useCallback((): void => {
    dispatch(getUserCertificates({ email, organizationId, statuses }));
    // Keyed by value so inline `statuses` arrays from callers keep this callback stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, email, organizationId, statusesKey]);

  useEffect(() => {
    if (!autoFetch) {
      return;
    }

    fetchCertificates();
  }, [autoFetch, fetchCertificates]);

  return {
    certificates: certificatesStore.data,
    loading: certificatesStore.loading,
    error: certificatesStore.error,
    fetchCertificates,
  };
};

interface UserCertificatesFetcherProps {
  email?: string;
  organizationId?: number;
  statuses?: CertificateStatusEnum[];
}

export function UserCertificatesFetcher({
  email,
  organizationId,
  statuses,
}: UserCertificatesFetcherProps): null {
  useUserCertificates({ email, organizationId, statuses });
  return null;
}
